import { useEffect, useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { ArrowLeftIcon, ArrowRightIcon, CheckCircleIcon } from "@heroicons/react/24/outline"
import { Layout } from "../components/Layout"
import { Button } from "../components/Button"
import { Input } from "../components/Form/Input"
import { ConfirmDialog } from "../components/Dialog/ConfirmDialog"
import { client, APIError } from "../api"
import { useUsers } from "../hooks/useUsers"
import { limitMessage, selectGreedy, withinLimits, type Counts } from "./diagramTransferSelection"
import { groupByBlocker, type Blocker } from "./diagramTransferBlockers"
import type {
  TransferRequest,
  previewDiagramTransfer,
} from "../../../backend/src/lib/diagram-team-transfer"

type Preview = Awaited<ReturnType<typeof previewDiagramTransfer>>
type InventoryDiagram = {
  public_id: string
  name: string
  movable: boolean
  reason: string | null
  blocker: Blocker | null
  note?: string
  component: string
  counts: Counts
}
type ApplyResult = {
  moved: number
  moved_related: number
  copied_formations: number
  replaced_formations: number
  cleanup_pending: boolean
  operation_public_id: string
  destination: string
}

const endpoint = client.admin["diagram-transfers"]
const select =
  "block border border-input bg-background text-primary rounded-md h-10 px-3 text-sm w-full"
// A preview token lives 15 minutes server-side; refresh well before that.
const PREVIEW_MAX_AGE_MS = 12 * 60 * 1000
const COLLAPSE_AFTER = 5

function message(error: unknown) {
  return error instanceof APIError
    ? error.originalError.message
    : error instanceof Error
      ? error.message
      : "Request failed"
}

// The RPC client already throws on a non-2xx response; checking again here means an
// error body can never be mistaken for a preview or a result if that ever changes.
async function readJson<T>(response: {
  ok: boolean
  status: number
  json: () => Promise<unknown>
}): Promise<T> {
  if (!response.ok) {
    let text = `Request failed (${response.status})`
    try {
      const body = (await response.json()) as { message?: string }
      if (body.message) text = body.message
    } catch {
      // keep the status text
    }
    throw new Error(text)
  }
  return (await response.json()) as T
}

function useDebounced<T>(value: T, ms: number) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms)
    return () => clearTimeout(timer)
  }, [value, ms])
  return debounced
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

export function DiagramTransfer() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const [search, setSearch] = useState("")
  const [user, setUser] = useState(params.get("creator") ?? "")
  const [source, setSource] = useState("")
  const [destination, setDestination] = useState("")
  const [picked, setPicked] = useState<string[]>([])
  const [names, setNames] = useState<Record<string, string>>({})
  const [appliedPicks, setAppliedPicks] = useState<string[]>([])
  const [limitNote, setLimitNote] = useState<string | null>(null)
  const [confirm, setConfirm] = useState(false)
  const [result, setResult] = useState<ApplyResult | null>(null)
  const queryClient = useQueryClient()
  const users = useUsers({ search, limit: 20 })

  const inventory = useQuery({
    queryKey: ["admin", "diagram-transfer-inventory", user, source],
    enabled: !!user,
    queryFn: async () =>
      readJson<{
        teams: { public_id: string; name: string; role: string }[]
        diagrams: InventoryDiagram[]
        has_more: boolean
      }>(
        await endpoint.inventory.$get({
          query: { user_public_id: user, source_team_public_id: source || undefined },
        })
      ),
  })
  const eligible =
    inventory.data?.teams.filter((t) => t.role === "admin" || t.role === "coach") ?? []
  const diagrams = useMemo(() => (source ? (inventory.data?.diagrams ?? []) : []), [
    source,
    inventory.data,
  ])
  const movable = diagrams.filter((d) => d.movable)
  const blocked = diagrams.filter((d) => !d.movable)
  const blockerGroups = useMemo(() => groupByBlocker(blocked), [blocked])
  // Only plays that can move are ever sent; a stale pick drops out when the list refreshes.
  const selected = useMemo(() => {
    const ok = new Set(movable.map((d) => d.public_id))
    return picked.filter((id) => ok.has(id))
  }, [picked, movable])
  const sourceName = eligible.find((t) => t.public_id === source)?.name
  const destinationName = eligible.find((t) => t.public_id === destination)?.name

  const request: TransferRequest = {
    include_related: true,
    user_public_id: user,
    source_team_public_id: source,
    destination_team_public_id: destination,
    diagram_public_ids: selected,
    formation_names: names,
  }
  const requestKey = JSON.stringify(request)
  const debouncedKey = useDebounced(requestKey, 400)
  const settled = debouncedKey === requestKey
  const ready = !!source && !!destination && source !== destination && selected.length > 0

  const preview = useQuery({
    queryKey: ["admin", "diagram-transfer-preview", debouncedKey],
    enabled: ready && settled,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      readJson<Preview>(await endpoint.preview.$post({ json: JSON.parse(debouncedKey) })),
  })
  // keepPreviousData would otherwise keep showing a summary for a different selection.
  const summary = ready && settled && preview.data ? preview.data : null
  const previewing = ready && (!settled || preview.isFetching)
  const previewError = ready && settled && !preview.isFetching ? preview.error : null

  function resetFlow() {
    setNames({})
    setResult(null)
    apply.reset()
    reconcile.reset()
  }

  const apply = useMutation({
    mutationFn: async (): Promise<ApplyResult> => {
      const current = summary
      if (!current) throw new Error("Choose plays and a destination team first.")
      let token = current.token
      if (Date.now() - preview.dataUpdatedAt > PREVIEW_MAX_AGE_MS) {
        // Stale preview: re-check silently, and only continue if nothing changed.
        const fresh = await readJson<Preview>(
          await endpoint.preview.$post({ json: JSON.parse(debouncedKey) })
        )
        const shape = (p: Preview) => JSON.stringify({ ...p, token: "" })
        if (shape(fresh) !== shape(current)) {
          queryClient.setQueryData(["admin", "diagram-transfer-preview", debouncedKey], fresh)
          throw new Error("The transfer changed since you reviewed it. Review the updated summary.")
        }
        token = fresh.token
      }
      const done = await readJson<Omit<ApplyResult, "destination">>(
        await endpoint.apply.$post({ json: { ...JSON.parse(debouncedKey), token, confirm: true } })
      )
      return { ...done, destination: current.destination.name }
    },
    onSuccess: (done) => {
      setResult(done)
      setPicked([])
 setLimitNote(null)
      setNames({})
        void queryClient.invalidateQueries({ queryKey: ["admin"] })
    },
    onError: () => {
      // Outcome unknown: reload the source inventory so the page shows where the plays are.
      // Any failure can follow a commit (the server reports a lost batch as a 409), so the
      // exact locate lookup decides what the message says.
      setAppliedPicks(selected)
      void inventory.refetch()
      // Whatever went wrong (expired, content changed), re-preview so the screen shows
      // the current truth; the message below says what happened.
      void preview.refetch()
    },
  })
  const located = useQuery({
    queryKey: ["admin", "diagram-transfer-locate", appliedPicks],
    enabled: !!apply.error && appliedPicks.length > 0,
    retry: 1,
    gcTime: 0,
    queryFn: async () =>
      readJson<{ diagrams: { public_id: string; team_public_id: string }[] }>(
        await endpoint.locate.$get({ query: { ids: appliedPicks.join(",") } })
      ),
  })
  // "moved" / "unmoved" only when every picked play agrees; anything else stays unknown.
  const outcome: "checking" | "moved" | "unmoved" | "unknown" = located.isFetching
    ? "checking"
    : located.data && located.data.diagrams.length === appliedPicks.length
      ? located.data.diagrams.every((d) => d.team_public_id === destination)
        ? "moved"
        : located.data.diagrams.every((d) => d.team_public_id === source)
          ? "unmoved"
          : "unknown"
      : "unknown"
  const reconcile = useMutation({
    mutationFn: async () => {
      if (!result) throw new Error("No transfer to finish")
      return readJson<unknown>(
        await endpoint.reconcile.$post({ json: { operation_public_id: result.operation_public_id } })
      )
    },
  })

  const extraPlays = summary
    ? summary.diagrams.filter((d) => !selected.includes(d.public_id))
    : []
  const blockers = summary?.blockers ?? []
  const canTransfer =
    !!summary && !blockers.length && !previewing && !apply.isPending && !!summary.token
  const showNoMovable = !!source && !inventory.isFetching && !!diagrams.length && !movable.length

  return (
    <Layout>
      <div className="max-w-4xl space-y-6">
        <div className="space-y-3">
          <Button variant="ghost" size="sm" className="-ml-3" onClick={() => navigate("/diagrams")}>
            <ArrowLeftIcon className="h-4 w-4" />
            Back to diagrams
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Transfer diagrams</h1>
            <p className="text-sm text-muted-foreground">
              Move a coach’s plays from one of their teams to another. Only plays that can move
              together with everything linked to them are selectable. Games and source formations
              stay in place.
            </p>
          </div>
        </div>

        <fieldset disabled={apply.isPending} className="space-y-5 min-w-0">
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block space-y-1 text-sm font-medium">
              Find coach
              <Input
                aria-label="Find coach"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or email"
              />
            </label>
            <label className="block space-y-1 text-sm font-medium">
              Coach
              <select
                aria-label="Coach"
                className={select}
                value={user}
                onChange={(e) => {
                  setUser(e.target.value)
                  setSource("")
                  setDestination("")
                  setPicked([])
 setLimitNote(null)
                  resetFlow()
                }}
              >
                <option value="">Choose a coach</option>
                {user && !users.data?.users.some((u) => u.public_id === user) && (
                  <option value={user}>Selected coach ({user})</option>
                )}
                {users.data?.users.map((u) => (
                  <option key={u.public_id} value={u.public_id}>
                    {u.name} ({u.email})
                  </option>
                ))}
              </select>
            </label>
          </div>
          {users.error && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{message(users.error)}</p>}
          {inventory.error && (
            <div role="alert" className="flex items-center gap-3 text-sm text-red-600 dark:text-red-400">
              {message(inventory.error)}
              <Button variant="outline" size="sm" onClick={() => void inventory.refetch()}>
                Try again
              </Button>
            </div>
          )}
          {user && !inventory.isFetching && !inventory.error && eligible.length < 2 && (
            <p className="text-sm text-muted-foreground">
              This coach needs coach or admin access to two teams.
            </p>
          )}
          <div className="grid sm:grid-cols-[1fr_auto_1fr] items-end gap-3">
            <label className="block space-y-1 text-sm font-medium">
              From team
              <select
                aria-label="From team"
                className={select}
                value={source}
                onChange={(e) => {
                  setSource(e.target.value)
                  if (e.target.value === destination) setDestination("")
                  setPicked([])
 setLimitNote(null)
                  resetFlow()
                }}
              >
                <option value="">Choose source</option>
                {eligible.map((t) => (
                  <option key={t.public_id} value={t.public_id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
            <ArrowRightIcon className="hidden sm:block h-5 w-5 mb-2.5 text-muted-foreground" />
            <label className="block space-y-1 text-sm font-medium">
              To team
              <select
                aria-label="To team"
                className={select}
                value={destination}
                onChange={(e) => {
                  setDestination(e.target.value)
                  setNames({})
                                setResult(null)
                }}
              >
                <option value="">Choose destination</option>
                {eligible
                  .filter((t) => t.public_id !== source)
                  .map((t) => (
                    <option key={t.public_id} value={t.public_id}>
                      {t.name}
                    </option>
                  ))}
              </select>
            </label>
          </div>

          {source && (
            <section className="rounded-lg border border-input p-4 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-semibold">
                  Plays by this coach in {sourceName ?? "this team"}
                </h2>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    {selected.length} of {movable.length} selected
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!movable.length || selected.length === movable.length}
                    onClick={() => {
                      const { ids, stopped } = selectGreedy(movable)
                      setPicked(ids)
                      setLimitNote(stopped ? limitMessage(ids.length, movable.length) : null)
                      resetFlow()
                    }}
                  >
                    Select all movable
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!selected.length}
                    onClick={() => {
                      setPicked([])
 setLimitNote(null)
                      resetFlow()
                    }}
                  >
                    Clear
                  </Button>
                </div>
              </div>
              {limitNote && (
                <p role="status" className="text-sm text-muted-foreground">
                  {limitNote}
                </p>
              )}
              {inventory.isFetching && !diagrams.length && (
                <p className="text-sm text-muted-foreground">Loading plays…</p>
              )}
              {inventory.data?.has_more && (
                <p className="text-sm text-muted-foreground">
                  Showing the first 200 plays. Move these first; the rest appear afterwards.
                </p>
              )}
              <div className="max-h-72 overflow-auto divide-y divide-input/60">
                {movable.map((d) => (
                  <label key={d.public_id} className="flex items-center gap-2 py-1.5 text-sm">
                    <input
                      type="checkbox"
                      checked={selected.includes(d.public_id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          const next = [...selected, d.public_id]
                          if (!withinLimits(movable.filter((m) => next.includes(m.public_id)))) {
                            setLimitNote(limitMessage(selected.length, movable.length))
                            return
                          }
                          setPicked(next)
                        } else setPicked(selected.filter((id) => id !== d.public_id))
                        setLimitNote(null)
                        resetFlow()
                      }}
                    />
                    <span>
                      {d.name}
                      {d.note && (
                        <span className="block text-xs text-muted-foreground">{d.note}</span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
              {!inventory.isFetching && !diagrams.length && !inventory.error && (
                <p className="text-sm text-muted-foreground">
                  This coach has no plays in {sourceName ?? "this team"}.
                </p>
              )}
              {showNoMovable && (
                <p className="text-sm text-muted-foreground">
                  None of these plays can move yet. The reasons are listed below.
                </p>
              )}
              {!!blocked.length && (
                <details open={blockerGroups.length <= COLLAPSE_AFTER} className="text-sm">
                  <summary className="cursor-pointer font-medium text-muted-foreground">
                    {blocked.length} can’t move
                  </summary>
                  <ul className="mt-2 max-h-80 overflow-auto divide-y divide-input/60">
                    {blockerGroups.map(({ blocker, plays }) => (
                      <li key={blocker.key} className="py-2">
                        <h3 className="font-semibold">
                          {blocker.label} blocks {plural(plays.length, "play")}
                        </h3>
                        <p className="text-xs text-muted-foreground">{blocker.hint}</p>
                        <details className="mt-1 text-xs">
                          <summary className="cursor-pointer text-primary">
                            Show the {plays.length === 1 ? "play" : "plays"}
                          </summary>
                          <ul className="mt-1 list-disc pl-5 space-y-0.5 text-muted-foreground">
                            {plays.map((d) => (
                              <li key={d.public_id}>{d.name}</li>
                            ))}
                          </ul>
                        </details>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </section>
          )}

          {source && selected.length > 0 && !destination && (
            <p className="text-sm text-muted-foreground">
              Choose a destination team to see what will move.
            </p>
          )}

          {previewError && (
            <div role="alert" className="rounded-lg border border-destructive/50 p-4 text-sm">
              <p className="font-medium text-red-600 dark:text-red-400">{message(previewError)}</p>
              <p className="mt-1 text-muted-foreground">
                Change the selection to try again.{" "}
                <button className="underline" onClick={() => void preview.refetch()}>
                  Check again
                </button>
              </p>
            </div>
          )}

          {ready && !previewError && !summary && (
            <p className="text-sm text-muted-foreground">Checking what will move…</p>
          )}

          {summary && (
            <section
              aria-label="Transfer summary"
              className="rounded-lg border border-input p-4 space-y-4 text-sm"
            >
              <div className="flex items-center justify-between gap-2">
                <h2 className="font-semibold text-base">
                  What will move: {summary.source.name} → {summary.destination.name}
                </h2>
                {previewing && <span className="text-muted-foreground">Updating…</span>}
              </div>

              <div>
                <p className="font-medium">
                  {plural(selected.length, "play")} you picked
                  {extraPlays.length > 0 && (
                    <>
                      , plus {plural(extraPlays.length, "more play")} that{" "}
                      {extraPlays.length === 1 ? "is" : "are"} linked to them
                    </>
                  )}
                </p>
                {extraPlays.length > 0 && (
                  <details className="mt-1">
                    <summary className="cursor-pointer text-primary">
                      Show the extra plays
                    </summary>
                    <ul className="list-disc pl-5 mt-1">
                      {extraPlays.map((d) => (
                        <li key={d.public_id}>{d.name}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>

              {summary.related.length > 0 && (
                <div>
                  <p className="font-medium">Collections that move with them</p>
                  <ul className="list-disc pl-5 mt-1">
                    {summary.related.map((r) => (
                      <li key={r.public_id}>
                        {r.personal && r.moves_with ? (
                          <>
                            Personal {r.kind} “{r.name}” moves to {summary.destination.name} with
                            its {r.moves_with}
                          </>
                        ) : (
                          <>
                            {r.kind}: {r.name}
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {summary.formations.length > 0 && (
                <div className="space-y-2">
                  <p className="font-medium">Formations</p>
                  {summary.formations.map((f) => (
                    <label key={f.public_id} className="block">
                      <span className="text-muted-foreground">
                        {f.name} —{" "}
                        {f.action === "reuse"
                          ? `matches a formation already on ${summary.destination.name}`
                          : f.action === "replace"
                            ? `replaces ${summary.destination.name}’s unused ${f.destination_name}. Type a different name to keep both.`
                            : `copied to ${summary.destination.name}`}
                      </span>
                      <Input
                        aria-label={`Destination name for ${f.name}`}
                        value={names[f.public_id] ?? f.destination_name}
                        onChange={(e) => setNames({ ...names, [f.public_id]: e.target.value })}
                      />
                    </label>
                  ))}
                </div>
              )}

              <div>
                <p className="font-medium">What changes</p>
                <ul className="list-disc pl-5 mt-1 space-y-0.5">
                  <li>
                    Teammates on {summary.source.name} lose access to these plays; teammates on{" "}
                    {summary.destination.name} gain it.
                  </li>
                  {summary.detached_film_count > 0 && (
                    <li>
                      {plural(summary.detached_film_count, "film attachment")} from other teams{" "}
                      {summary.detached_film_count === 1 ? "is" : "are"} detached. The games and
                      video files stay where they are.
                    </li>
                  )}
                  {summary.reset_public_links > 0 && (
                    <li>
                      {plural(summary.reset_public_links, "public play link")}{" "}
                      {summary.reset_public_links === 1 ? "becomes" : "become"} team-only.
                    </li>
                  )}
                  {summary.disabled_playbook_links > 0 && (
                    <li>
                      {plural(summary.disabled_playbook_links, "public playbook link")}{" "}
                      {summary.disabled_playbook_links === 1 ? "is" : "are"} disabled.
                    </li>
                  )}
                </ul>
              </div>

              {blockers.length > 0 && (
                <div role="alert" className="rounded-md border border-destructive/50 p-3">
                  <p className="font-medium text-red-600 dark:text-red-400">This can’t be transferred yet</p>
                  <ul className="list-disc pl-5 mt-1">
                    {blockers.map((b) => (
                      <li key={b}>{b}</li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          )}

          {ready && (
            <div className="flex items-center gap-3">
              <Button disabled={!canTransfer} onClick={() => setConfirm(true)}>
                {apply.isPending
                  ? "Transferring…"
                  : `Transfer ${plural(summary?.diagrams.length ?? selected.length, "play")} to ${destinationName ?? "team"}`}
              </Button>
            </div>
          )}
        </fieldset>

        {apply.error && (
          <div role="alert" className="rounded-lg border border-destructive/50 p-4 text-sm">
            <p className="font-medium text-red-600 dark:text-red-400">{message(apply.error)}</p>
            <p className="mt-1 text-muted-foreground">
              {outcome === "checking"
                ? "Checking where the picked plays are now."
                : outcome === "moved"
                  ? `All picked plays are now in ${destinationName ?? "the destination team"}, so the transfer went through.`
                  : outcome === "unmoved"
                    ? `Nothing was moved: all picked plays are still in ${sourceName ?? "the source team"}. The summary above has been refreshed.`
                    : "The picked plays are not all in one team, or could not be checked, so the outcome is unknown."}
            </p>
            {outcome !== "unmoved" && outcome !== "checking" && (
              <p className="mt-1 text-muted-foreground">
                Find the audit entry with action diagrams.transfer.started to confirm the outcome and
                finish cleanup.
              </p>
            )}
          </div>
        )}

        {result && (
          <div role="status" className="rounded-lg border border-green-200 p-4 space-y-2 text-sm">
            <p className="flex items-center gap-2 font-medium text-green-800">
              <CheckCircleIcon className="h-5 w-5" />
              Moved {plural(result.moved, "play")} to {result.destination}
            </p>
            <p>
              Moved {plural(result.moved_related, "linked item")} and copied{" "}
              {plural(result.copied_formations, "formation")}
              {result.replaced_formations > 0 &&
                ` and replaced ${plural(result.replaced_formations, "unused formation")}`}
              . Audit reference:{" "}
              {result.operation_public_id}
            </p>
            {result.cleanup_pending && !reconcile.data && (
              <div className="space-y-2">
                <p>The plays moved; finish the upload-context and audit cleanup.</p>
                <Button
                  size="sm"
                  disabled={reconcile.isPending}
                  onClick={() => reconcile.mutate()}
                >
                  Finish cleanup
                </Button>
              </div>
            )}
            {reconcile.data !== undefined && <p>Cleanup completed.</p>}
            {reconcile.error && (
              <p role="alert" className="text-red-600 dark:text-red-400">
                {message(reconcile.error)}
              </p>
            )}
            <p className="text-muted-foreground">
              Pick more plays above to run another transfer.
            </p>
          </div>
        )}

        <ConfirmDialog
          open={confirm}
          onOpenChange={setConfirm}
          title={`Transfer ${plural(summary?.diagrams.length ?? 0, "play")}?`}
          description={
            summary
              ? `Move ${plural(summary.diagrams.length, "play")} and ${plural(summary.related.length, "linked collection")} from ${summary.source.name} to ${summary.destination.name}. Teammates on ${summary.source.name} lose access.${summary.detached_film_count ? ` ${plural(summary.detached_film_count, "film attachment")} will be detached.` : ""}${summary.reset_public_links ? ` ${plural(summary.reset_public_links, "public play link")} will become team-only.` : ""}${summary.disabled_playbook_links ? ` ${plural(summary.disabled_playbook_links, "public playbook link")} will be disabled.` : ""}`
              : ""
          }
          confirmLabel="Confirm transfer"
          onConfirm={() => apply.mutate()}
        />
      </div>
    </Layout>
  )
}
